/* eslint-disable react/jsx-no-useless-fragment */
import { Button, Stack, Typography } from '@mui/material';
import { Accordion } from '@common/components/Accordion';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { HeaderTitle } from '@/modules/simulation/components/HeaderTitle';
import { SimulationGroup } from '@thive/fddm-types';
import { InfoText } from '@/modules/common/components/InfoText';
import { ListPanelCard } from '@/modules/simulation/components/ListPanel/ListPanelCard';
import { SimulationUtil } from '@/modules/simulation/SimulationUtil';
import { SimulationServiceFactory } from '@/modules/simulation/service/SimulationServiceFactory';
import SelectedItemsInfo from '@/modules/simulation/components/SelectedSimulationsInfo';
import { UIBlockingLoadIndicator } from '@/modules/simulation/components/UIBlockingLoadIndicator';
import { UserPreferenceName } from '@/modules/userPreferences';

type Props = {
  onShowDetails: (simulationGroup: SimulationGroup) => void;
  onAddSimulationGroup: (name: string) => void;
  onEditSimulationGroup: (simulationGroup: SimulationGroup, name: string) => void;
};

export function ListPanel({ onShowDetails, onAddSimulationGroup, onEditSimulationGroup }: Props) {
  const { t } = useTranslation('interface');
  const simSvc = useMemo(
    () => SimulationServiceFactory.getInstance().createSimulationService(),
    [],
  );
  const runningSimulationsUpdateDelayInMs = 10000;

  const [simulationGroups, setSimulationGroups] = useState<SimulationGroup[]>();
  const [nextSimulationName, setNextSimulationName] = useState<string>('');
  const [loading, setLoading] = useState<boolean>(false);
  const [selectedSimulationGroups, setSelectedSimulationGroups] = useState<SimulationGroup[]>([]);
  const [allSelected, setAllSelected] = useState<boolean>(false);

  const load = useCallback(
    async (showLoadingIndicator: boolean) => {
      try {
        if (showLoadingIndicator) {
          setLoading(true);
        }

        const records = await simSvc.getSimulationGroups();
        setSimulationGroups(records);
        setNextSimulationName(SimulationUtil.getNextSimulationName(records));
      } catch {
        setSimulationGroups([]);
      } finally {
        if (showLoadingIndicator) {
          setLoading(false);
        }
      }
    },
    [simSvc],
  );

  const onDetails = useCallback(
    async (simulationGroup: SimulationGroup) => {
      onShowDetails(simulationGroup);
    },
    [onShowDetails],
  );

  const onAdd = useCallback(async () => {
    onAddSimulationGroup(nextSimulationName);
  }, [onAddSimulationGroup, nextSimulationName]);

  const onEdit = useCallback(
    async (simulationGroup: SimulationGroup) => {
      onEditSimulationGroup(simulationGroup, nextSimulationName);
    },
    [onEditSimulationGroup, nextSimulationName],
  );

  const onRerun = useCallback(
    async (group: SimulationGroup) => {
      setLoading(true);
      const nextGroup = await simSvc.reRunSimulationGroup(group.id);
      if (nextGroup) {
        load(true);
      } else {
        setLoading(false);
      }
    },
    [simSvc, load],
  );

  const onAbort = useCallback(
    async (simulationGroup: SimulationGroup) => {
      try {
        setLoading(true);
        await simSvc.abortSimulationGroup(simulationGroup.id);
        simulationGroup.status = 'ABORTING';
        setSimulationGroups((prev) =>
          prev?.map((g) => (g.id === simulationGroup.id ? simulationGroup : g)),
        );
      } finally {
        setLoading(false);
      }
    },
    [simSvc],
  );

  const onDelete = useCallback(
    async (group: SimulationGroup) => {
      simSvc.deleteSimulationGroup(group.id);

      const changedSet = simulationGroups?.filter((p) => p.id !== group.id) ?? [];
      setSimulationGroups(changedSet);
      setNextSimulationName(SimulationUtil.getNextSimulationName(changedSet));
    },
    [simSvc, simulationGroups],
  );

  const onSelect = useCallback(async (group: SimulationGroup, isSelected: boolean) => {
    setSelectedSimulationGroups((prevSelected) => {
      if (isSelected) {
        return [...prevSelected, group];
      }

      return prevSelected.filter((g) => g.id !== group.id);
    });
  }, []);

  const onSelectAll = useCallback(async () => {
    setAllSelected(!allSelected);
    setSelectedSimulationGroups(() => {
      if (!allSelected) {
        return simulationGroups ?? [];
      }
      return [];
    });
  }, [allSelected, simulationGroups]);

  const onDeleteSelected = useCallback(async () => {
    setLoading(true);
    try {
      selectedSimulationGroups.forEach((simulation) => simSvc.deleteSimulationGroup(simulation.id));

      const changedSet =
        simulationGroups?.filter((p) => !selectedSimulationGroups.includes(p)) ?? [];
      setSimulationGroups(changedSet);
      setNextSimulationName(SimulationUtil.getNextSimulationName(changedSet));
    } finally {
      setSelectedSimulationGroups([]);
      setAllSelected(false);
      setLoading(false);
    }
  }, [simSvc, simulationGroups, selectedSimulationGroups]);

  const simulationGroupsRef = useRef(simulationGroups);
  useEffect(() => {
    simulationGroupsRef.current = simulationGroups;
  }, [simulationGroups]);

  useEffect(() => {
    // PLZ FIX ME
    // eslint-disable-next-line react-hooks/set-state-in-effect
    load(true).catch(() => undefined);
  }, [load]);

  useEffect(() => {
    const interval = setInterval(async () => {
      const runningGroups = simulationGroupsRef.current?.filter(
        (p) => SimulationUtil.isRunningGroup(p) || p.status === 'ABORTING',
      );

      if (!runningGroups?.length) return;

      try {
        const updates = await Promise.all(
          runningGroups.map((g) => simSvc.getSimulationGroup(g.id)),
        );

        setSimulationGroups((prev) => prev?.map((g) => updates.find((u) => u?.id === g.id) ?? g));
      } catch {
        // no-op while switching away from simulation/visualization views
      }
    }, runningSimulationsUpdateDelayInMs);

    return () => clearInterval(interval);
  }, [simSvc]);

  if (loading) {
    return <UIBlockingLoadIndicator block={loading} />;
  }

  return (
    <>
      <Stack sx={{ pb: 2, px: 2 }} gap={1}>
        <HeaderTitle value={nextSimulationName} />
        <Typography sx={{ fontSize: '10px' }}>
          {/* t('simulation.run_card.version', 'TODO') */}
        </Typography>
        <Button onClick={onAdd}>{t('simulation.simulation_list.add_button.label')}</Button>
      </Stack>
      <Accordion
        persistId={UserPreferenceName.ACCORDION_SIMULATION_LIST}
        title={t('simulation.simulation_list.header')}
        sx={{ px: 2 }}
      >
        <SelectedItemsInfo
          nrOfSelectedSimulations={selectedSimulationGroups.length}
          sx={{ margin: '15px 0 20px' }}
          onSelectAll={onSelectAll}
          onDeleteSelected={onDeleteSelected}
        />
        {simulationGroups?.length === 0 && (
          <InfoText
            header={t('simulation.simulation_list.no_simulations.title')}
            subHeader={t('simulation.simulation_list.no_simulations.subtitle')}
            sx={{ mt: 4, mb: 4, ml: 2, mr: 2 }}
          />
        )}
        {simulationGroups?.map((group) => (
          <ListPanelCard
            key={group.id}
            simulationGroup={group}
            isSelected={allSelected}
            onSelect={onSelect}
            onDetails={onDetails}
            onEdit={onEdit}
            onRerun={onRerun}
            onAbort={onAbort}
            onDelete={onDelete}
          />
        ))}
      </Accordion>
      <>{/* Intentionally Empty */}</>
    </>
  );
}
